import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useRef } from "react";
import jsPDF from "jspdf";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { ArrowLeft, Printer, Activity, User, ShieldAlert, Calendar } from "lucide-react";
import { CompanyLogo, formattedDateTime } from "../../../Components/Utils";
import { Button } from "../../../Components/Buttons";
import { MultiDocumentSelect } from "../../../Components/Form";
import { useFetchLatestContactSettingQuery } from "../../../stores/api/contactSettings.api";
import { useSiteSettings } from "../../../contexts/SiteSettingsContext";


const PatientDetails = () => {
  const { system_number } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef();
  const siteSettings = useSiteSettings();
  const { contact: contactDetails, website } = siteSettings;
  const patient = location.state?.patient_details;
  console.log("Site Details:", siteSettings);
  // Modern Vector PDF Generator (Single Page, Monochromatic & Clean UI)
  const getBase64Image = (imgElement) => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = imgElement.naturalWidth || imgElement.width;
      canvas.height = imgElement.naturalHeight || imgElement.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(imgElement, 0, 0);
      return canvas.toDataURL("image/png");
    } catch (e) {
      return null;
    }
  };


  const loadImageAsDataUrl = (url) =>
    new Promise((resolve) => {
      if (!url) {
        resolve(null);
        return;
      }
      const img = new Image();
      img.crossOrigin = "anonymous"; // allow canvas export when server sends CORS headers
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } catch (e) {
          console.warn("Logo could not be converted to base64 (CORS?):", e);
          resolve(null);
        }
      };
      img.onerror = () => {
        console.warn("Logo image failed to load:", url);
        resolve(null);
      };
      img.src = url;
    });

  const handleDownloadPDF = async () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const margin = 12;
    const contentWidth = pageWidth - margin * 2;
    let currentY = margin;

    // Resolve the logo BEFORE drawing anything — addImage needs base64 data,
    // not a bare URL, so this must be awaited first.
    let logoDataUrl = siteSettings?.website?.header_logo ? await loadImageAsDataUrl(siteSettings?.website?.header_logo) : null;

    // ----------------------------------------------------------------------
    // CORPORATE COLOR PALETTE — green clinical brand, anchored on #63d38c
    // ----------------------------------------------------------------------
    const COLORS = {
      primary: [99, 211, 140],     // #63d38c — brand green (header band)
      primaryDark: [27, 122, 73],  // deep forest green — accents / dark text on green
      accentLight: [232, 248, 240],// pale mint tint for soft backgrounds
      slateDark: [30, 41, 59],     // near-black text
      slateMed: [71, 85, 105],     // secondary text
      slateLight: [148, 163, 184], // muted/placeholder text
      borderGray: [221, 230, 226], // soft mint-gray card borders
      rowAlt: [243, 250, 246],     // zebra-striping (mint tinted)
      danger: [190, 24, 54],       // blood group / alerts
      success: [27, 122, 73],      // positive status badges (forest green)
      white: [255, 255, 255],
    };

    const setText = (rgb) => pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
    const setDraw = (rgb) => pdf.setDrawColor(rgb[0], rgb[1], rgb[2]);
    const setFill = (rgb) => pdf.setFillColor(rgb[0], rgb[1], rgb[2]);

    const safe = (v, fallback = "—") =>
      v === null || v === undefined || v === "" ? fallback : v;

    const formatDate = (d) =>
      d ? new Date(d).toLocaleDateString("en-GB") : "—";

    const formatDateTime = (d) =>
      d ? new Date(d).toLocaleString("en-GB") : "—";

    // ----------------------------------------------------------------------
    // WATERMARK — diagonal "CONFIDENTIAL" stamped faintly behind content
    // ----------------------------------------------------------------------
    const drawWatermark = () => {
      pdf.saveGraphicsState && pdf.saveGraphicsState();
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(52);
      setText(COLORS.borderGray);
      if (pdf.GState) {
        pdf.setGState(new pdf.GState({ opacity: 0.3 }));
      }
      pdf.text("CONFIDENTIAL", pageWidth / 2, pageHeight / 2, {
        align: "center",
        angle: 35,
      });
      if (pdf.GState) {
        pdf.setGState(new pdf.GState({ opacity: 1 }));
      }
      pdf.restoreGraphicsState && pdf.restoreGraphicsState();
    };

    const headerH = 28; // Perfect height for this clean 3-column layout
    const drawPageHeader = () => {
      setDraw(COLORS.primary);
      pdf.setLineWidth(0.8);
      pdf.line(0, headerH, pageWidth, headerH);
      pdf.setLineWidth(0.25);

      // Width and position settings based on image_fe4d70.png
      const logoWidth = 35;
      const logoHeight = 13;
      
      const rightCardWidth = 46;
      const rightCardHeight = 18;
      const rightCardX = pageWidth - margin - rightCardWidth;
      const rightCardY = 5;

      // ----------------------------------------------------------------------
      // 1. LEFT COLUMN: LOGO IMAGE
      // ----------------------------------------------------------------------
      if (logoDataUrl) {
        pdf.addImage(
          logoDataUrl,
          "PNG",
          margin,
          7, // Centered vertically in the header space
          logoWidth,
          logoHeight,
          undefined,
          "FAST"
        );
      }

      // ----------------------------------------------------------------------
      // 2. MIDDLE COLUMN: LARGE WRAPPING HEADING & SUBTITLES
      // ----------------------------------------------------------------------
      const textX = margin + logoWidth + 6; // Starts right after the logo
      const textWidth = rightCardX - textX - 6; // Available dynamic space in the middle

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11.5);
      setText(COLORS.slateDark); // Dark slate for premium corporate look
      
      const titleLines = pdf.splitTextToSize(safe(website?.website_name), textWidth);
      pdf.text(titleLines, textX, 8);

      // Subtitles dynamically push down based on title wrap lines
      const titleHeightOffset = titleLines.length * 4.8;
      
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.5);
      setText(COLORS.success); // Clean green accent text for subtitle
      pdf.text("OFFICIAL CLINICAL MEDICAL RECORD", textX, 7 + titleHeightOffset);
      
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(6.5);
      setText(COLORS.slateLight);
      pdf.text(`Generated Date | ${formatDate(new Date())}`, textX, 11 + titleHeightOffset);

      // ----------------------------------------------------------------------
      // 3. RIGHT COLUMN: FILLED REGISTRATION & ID CARD
      // ----------------------------------------------------------------------
      // Soft background tint box
      setFill(COLORS.rowAlt);
      setDraw(COLORS.borderGray);
      pdf.setLineWidth(0.2);
      pdf.roundedRect(rightCardX, rightCardY, rightCardWidth, rightCardHeight, 2, 2, "FD");

      // Top Half: Registration Date
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(5.8);
      setText(COLORS.slateLight);
      pdf.text("REGISTRATION DATE", rightCardX + rightCardWidth - 4, rightCardY + 4.5, { align: "right" });

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8.5);
      setText(COLORS.slateDark);
      pdf.text(formatDate(patient?.reg_date), rightCardX + rightCardWidth - 4, rightCardY + 8.2, { align: "right" });

      // Middle Thin Partition Line
      setDraw(COLORS.borderGray);
      pdf.setLineWidth(0.15);
      pdf.line(rightCardX + 4, rightCardY + 10, rightCardX + rightCardWidth - 4, rightCardY + 10);

      // Bottom Half: Tracking Identifier
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(5.8);
      setText(COLORS.slateLight);
      pdf.text("TRACKING IDENTIFIER", rightCardX + rightCardWidth - 4, rightCardY + 13.2, { align: "right" });

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      setText(COLORS.success); // Green accent for the ID number
      pdf.text(safe(patient?.system_number), rightCardX + rightCardWidth - 4, rightCardY + 16.8, { align: "right" });

      // Reset Y-Pointer smoothly for content below header line
      currentY = headerH + 6;
      drawWatermark();
    };
 
    const FOOTER_RESERVE = 14;
    const fitsOnPage = (neededSpace) =>
      currentY + neededSpace <= pageHeight - FOOTER_RESERVE;

    // ----------------------------------------------------------------------
    // SECTION TITLE — colored bar + bold caption, consistent across sections
    // ----------------------------------------------------------------------
    const drawSectionTitle = (label) => {
      setFill(COLORS.primary);
      pdf.rect(margin, currentY, 2, 4.4, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      setText(COLORS.slateDark);
      pdf.text(label.toUpperCase(), margin + 4, currentY + 3.6);
      currentY += 6.5;
    };

    // ----------------------------------------------------------------------
    // BORDERED CARD with a 2-column x N-row label/value grid.
    // rows = [[label, value, label2, value2], ...]  (label2/value2 optional)
    // ----------------------------------------------------------------------
    const drawInfoCard = (rows) => {
      const rowH = 6.5;
      const cardH = rows.length * rowH;
      if (!fitsOnPage(cardH + 2)) return;

      setDraw(COLORS.borderGray);
      setFill(COLORS.white);
      pdf.setLineWidth(0.25);
      pdf.roundedRect(margin, currentY, contentWidth, cardH, 1.3, 1.3, "FD");

      const col1Label = margin + 3;
      const col2Label = margin + contentWidth / 2 + 3;

      // Dynamically size the gap between label and value per column so long
      // labels (e.g. "Residential Address", "Patient Classification") never
      // collide with the value text — measured against ALL rows in this card.
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.6);
      let maxLabel1Width = 0;
      let maxLabel2Width = 0;
      rows.forEach((row) => {
        maxLabel1Width = Math.max(maxLabel1Width, pdf.getTextWidth(row[0] || ""));
        if (row.length > 2 && row[2]) {
          maxLabel2Width = Math.max(maxLabel2Width, pdf.getTextWidth(row[2]));
        }
      });
      const col1Value = col1Label + maxLabel1Width + 5;
      const col2Value = col2Label + maxLabel2Width + 5;

      rows.forEach((row, idx) => {
        const rowY = currentY + idx * rowH;

        if (idx % 2 === 1) {
          setFill(COLORS.rowAlt);
          pdf.rect(margin, rowY, contentWidth, rowH, "F");
        }
        if (idx > 0) {
          setDraw(COLORS.borderGray);
          pdf.setLineWidth(0.15);
          pdf.line(margin, rowY, margin + contentWidth, rowY);
        }
        setDraw(COLORS.borderGray);
        pdf.line(
          margin + contentWidth / 2,
          rowY,
          margin + contentWidth / 2,
          rowY + rowH
        );

        const textY = rowY + 4.5;
        pdf.setFontSize(7.6);

        pdf.setFont("helvetica", "bold");
        setText(COLORS.slateMed);
        pdf.text(row[0], col1Label, textY);

        pdf.setFont("helvetica", "normal");
        setText(COLORS.slateDark);
        pdf.text(String(safe(row[1])), col1Value, textY, {
          maxWidth: margin + contentWidth / 2 - col1Value - 3,
        });

        if (row.length > 2 && row[2]) {
          pdf.setFont("helvetica", "bold");
          setText(COLORS.slateMed);
          pdf.text(row[2], col2Label, textY);

          pdf.setFont("helvetica", "normal");
          setText(COLORS.slateDark);
          pdf.text(String(safe(row[3])), col2Value, textY, {
            maxWidth: margin + contentWidth - col2Value - 3,
          });
        }
      });

      setDraw(COLORS.borderGray);
      pdf.setLineWidth(0.25);
      pdf.roundedRect(margin, currentY, contentWidth, cardH, 1.3, 1.3, "D");

      currentY += cardH + 5;
    };

    // ----------------------------------------------------------------------
    // FULL-WIDTH WRAPPING FIELD — used for values that can run long (e.g.
    // Residential Address) where a 2-column row would get cramped. Renders
    // a bordered single-row box that grows in height to fit wrapped text.
    // ----------------------------------------------------------------------
    const drawFullWidthField = (label, value) => {
      const labelX = margin + 3;
      const lineHeight = 3.6;
      const topPad = 4.5;
      const bottomPad = 2.6;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.6);
      const labelWidth = pdf.getTextWidth(label);
      const valueX = labelX + labelWidth + 5;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.6);
      const wrapped = pdf.splitTextToSize(
        String(safe(value)),
        contentWidth - (valueX - margin) - 3
      );

      const boxH = topPad + bottomPad + (wrapped.length - 1) * lineHeight;

      if (!fitsOnPage(boxH + 2)) return;

      setDraw(COLORS.borderGray);
      setFill(COLORS.white);
      pdf.setLineWidth(0.25);
      pdf.roundedRect(margin, currentY, contentWidth, boxH, 1.3, 1.3, "FD");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.6);
      setText(COLORS.slateMed);
      pdf.text(label, labelX, currentY + topPad);

      pdf.setFont("helvetica", "normal");
      setText(COLORS.slateDark);
      pdf.text(wrapped, valueX, currentY + topPad);

      currentY += boxH + 5;
    };

    // ----------------------------------------------------------------------
    // VITAL SIGNS — compact card-style tiles (6 across), colored accents
    // ----------------------------------------------------------------------
    const drawVitalsCards = (vitals) => {
      const tileGap = 2.2;
      const tileW = (contentWidth - tileGap * (vitals.length - 1)) / vitals.length;
      const tileH = 14;
      if (!fitsOnPage(tileH + 2)) return;

      vitals.forEach((v, idx) => {
        const x = margin + idx * (tileW + tileGap);

        setFill(COLORS.white);
        setDraw(COLORS.borderGray);
        pdf.setLineWidth(0.25);
        pdf.roundedRect(x, currentY, tileW, tileH, 1, 1, "FD");

        setFill(v.color);
        pdf.rect(x, currentY, 1.3, tileH, "F");

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(5.8);
        setText(COLORS.slateLight);
        pdf.text(v.label.toUpperCase(), x + 3, currentY + 4.7, {
          maxWidth: tileW - 4,
        });

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9.5);
        setText(v.danger ? COLORS.danger : COLORS.slateDark);
        pdf.text(String(safe(v.val, "—")), x + 3, currentY + 10.8);

        if (v.val && v.unit) {
          const valWidth = pdf.getTextWidth(String(v.val));
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(6);
          setText(COLORS.slateLight);
          pdf.text(v.unit, x + 3 + valWidth + 1.3, currentY + 10.8);
        }
      });

      currentY += tileH + 5.5;
    };

    const drawTable = (columns, allRows, emptyMessage, maxRows = 5) => {
      const rowH = 5.6;

      if (!fitsOnPage(rowH + 2)) return;

      setFill(COLORS.accentLight);
      pdf.rect(margin, currentY, contentWidth, rowH, "F");
      setDraw(COLORS.primary);
      pdf.setLineWidth(0.4);
      pdf.line(margin, currentY + rowH, margin + contentWidth, currentY + rowH);
      pdf.setLineWidth(0.15);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(6.8);
      setText(COLORS.primaryDark);
      columns.forEach((col) => {
        pdf.text(col.label, margin + col.x, currentY + 3.9, {
          align: col.align || "left",
        });
      });
      currentY += rowH;

      if (!allRows || allRows.length === 0) {
        if (!fitsOnPage(rowH + 2)) return;
        setFill(COLORS.rowAlt);
        pdf.rect(margin, currentY, contentWidth, rowH, "F");
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(7);
        setText(COLORS.slateLight);
        pdf.text(emptyMessage, margin + 4, currentY + 3.9);
        currentY += rowH + 4;
        return;
      }

      // Cap visible rows to guarantee single-page output; show a "+N more" note.
      const visibleRows = allRows.slice(0, maxRows);

      visibleRows.forEach((rowData, idx) => {
        if (!fitsOnPage(rowH + 2)) return;

        if (idx % 2 === 1) {
          setFill(COLORS.rowAlt);
          pdf.rect(margin, currentY, contentWidth, rowH, "F");
        }
        setDraw(COLORS.borderGray);
        pdf.setLineWidth(0.15);
        pdf.line(margin, currentY + rowH, margin + contentWidth, currentY + rowH);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7);
        setText(COLORS.slateDark);

        columns.forEach((col, colIdx) => {
          pdf.text(String(safe(rowData[colIdx])), margin + col.x, currentY + 3.9, {
            align: col.align || "left",
            maxWidth: col.width,
          });
        });

        currentY += rowH;
      });

      if (allRows.length > maxRows && fitsOnPage(rowH)) {
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(6.5);
        setText(COLORS.slateLight);
        pdf.text(
          `+ ${allRows.length - maxRows} more record(s) not shown — see full system log.`,
          margin + 2,
          currentY + 3.8
        );
        currentY += rowH;
      }

      currentY += 4;
    };

    // ----------------------------------------------------------------------
    // SIGNATURE / STAMP / QR placeholder block — compact sign-off row
    // ----------------------------------------------------------------------
    const drawSignOffBlock = () => {
      const blockH = 22;
      if (!fitsOnPage(blockH + 2)) return;

      const colW = contentWidth / 3;

      setDraw(COLORS.borderGray);
      pdf.setLineWidth(0.2);
      pdf.line(margin + 4, currentY + 13, margin + colW - 6, currentY + 13);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(6.8);
      setText(COLORS.slateMed);
      pdf.text("Attending Physician Signature", margin + 4, currentY + 17.5);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(6.5);
      setText(COLORS.slateDark);
      pdf.text(safe(patient?.physician?.name), margin + 4, currentY + 7);

      // Hospital stamp placeholder — centered in the remaining two-thirds of
      // the row now that the QR verification block has been removed.
      const stampX = margin + colW + colW;
      setDraw(COLORS.borderGray);
      pdf.setLineWidth(0.3);
      pdf.circle(stampX, currentY + 9, 9, "D");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(5.3);
      setText(COLORS.slateLight);
      pdf.text("OFFICIAL HOSPITAL", stampX, currentY + 8, { align: "center" });
      pdf.text("STAMP", stampX, currentY + 11, { align: "center" });

      currentY += blockH;
    };

    // ----------------------------------------------------------------------
    // FOOTER — drawn once at the fixed bottom position (single page report)
    // ----------------------------------------------------------------------
    const drawFooter = () => {
      const footerY = pageHeight - 11;

      setDraw(COLORS.borderGray);
      pdf.setLineWidth(0.25);
      pdf.line(margin, footerY, pageWidth - margin, footerY);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(6.3);
      setText(COLORS.slateLight);
      pdf.text(
        `Office: ${safe(contactDetails?.email)}  •  Support: ${safe(contactDetails?.phone)}`,
        margin,
        footerY + 3.8
      );

      pdf.setFont("helvetica", "bold");
      setText(COLORS.slateMed);
      pdf.text(
        "CONFIDENTIAL MEDICAL RECORD",
        pageWidth - margin,
        footerY + 3.8,
        { align: "right" }
      );

      pdf.setFont("helvetica", "normal");
      setText(COLORS.slateLight);
      pdf.text(
        `Page 1 of ${pdf.internal.getNumberOfPages()}`,
        pageWidth / 2,
        footerY + 3.8,
        { align: "center" }
      );
    };

    // =======================================================================
    // BEGIN DOCUMENT BUILD — everything below is sized to fit one A4 page
    // =======================================================================
    drawPageHeader();

    // --- SECTION 1: PATIENT DEMOGRAPHICS -----------------------------------
    drawSectionTitle("1. Patient Demographics");
    drawInfoCard([
      ["Full Name", patient?.patient_name, "Gender / Age", `${safe(patient?.gender)} / ${patient?.age ? `${patient.age} Yrs` : "—"}`],
      ["Relative Reference", patient?.father_husband_name, "National ID / CNIC", patient?.patient_cnic],
      ["Mobile Contact", patient?.mobile],
    ]);
    drawFullWidthField("Residential Address", patient?.address);

    // --- SECTION 2: CLINICAL CONTEXT ---------------------------------------
    drawSectionTitle("2. Clinical Context");
    drawInfoCard([
      ["Consultant Physician", patient?.physician?.name, "Assigned Unit", patient?.unit?.name],
      ["District Region", patient?.unit?.district?.name, "Patient Classification", (patient?.patient_type || "Standard Outpatient").toUpperCase()],
    ]);

    // --- SECTION 3: PHYSIOLOGICAL VITAL SIGNS ------------------------------
    drawSectionTitle("3. Physiological Vital Signs");
    drawVitalsCards([
      { label: "Core Temp", val: patient?.temperature, unit: "°F", color: [234, 88, 12] },
      { label: "Pulse Rate", val: patient?.pulse, unit: "BPM", color: COLORS.danger },
      { label: "Blood Pressure", val: patient?.bp, unit: "mmHg", color: [124, 58, 237] },
      { label: "Height", val: patient?.height, unit: "cm", color: COLORS.primaryDark },
      { label: "Weight", val: patient?.weight, unit: "kg", color: COLORS.success },
      { label: "Blood Group", val: patient?.blood_group, unit: "", color: COLORS.danger, danger: true },
    ]);

    // --- SECTION 4: CASE ADMISSION LOGISTICS -------------------------------
    drawSectionTitle("4. Case Admission Logistics");
    drawInfoCard([
      [
        patient?.patient_type === "icu" ? "Critical Care Ward" : "Neonatal Care Ward",
        `${safe(patient?.ward_type)} / Bed #${safe(patient?.bed)}`,
        "Admission Date/Time",
        formatDateTime(patient?.admission_date_time),
      ],
      [
        "Discharge Date/Time",
        formatDateTime(patient?.discharge_date_time),
        "",
        "",
      ],
    ]);

    // --- SECTION 5: CLINICAL EVALUATION HISTORY (VISITS LOOP) -------------
    drawSectionTitle("5. Clinical Evaluation History Log");
    const visitColumns = [
      { label: "INDEX", x: 3, width: 16 },
      { label: "EVALUATION / VISIT DATE", x: 20, width: 55 },
      { label: "NEXT PLANNED CLINICAL TARGET", x: 90, width: 75 },
    ];
    const visitRows = (patient?.patient_visits || []).map((v, index) => [
      String(index + 1).padStart(2, "0"),
      v.visit_date
        ? new Date(v.visit_date).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
        : "—",
      v.next_visit_plan
        ? new Date(v.next_visit_plan).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
        : "—",
    ]);
    drawTable(
      visitColumns,
      visitRows,
      "No visit entries tracked for this patient profile.",
      4
    );

    // --- SECTION 6: ASSOCIATED ATTACHMENTS (LOOP) --------------------------
    drawSectionTitle("6. Associated Dossier Attachments");
    const attachmentColumns = [
      { label: "#", x: 3, width: 10 },
      { label: "FILE NAME", x: 14, width: 95 },
      { label: "TYPE", x: 140, width: 30 },
    ];
    const attachmentRows = (patient?.attachments || []).map((file, index) => [
      String(index + 1),
      file.name || "Open Record Link",
      file.type || file.extension || "Document",
    ]);
    drawTable(
      attachmentColumns,
      attachmentRows,
      "No structural scan records mapped in this context sheet.",
      3
    );

    // --- SECTION 7: CLINICAL NOTES & IMPRESSIONS ---------------------------
    drawSectionTitle("7. Clinical Notes & Observations");
    const notesH = 15;
    if (fitsOnPage(notesH + 2)) {
      setDraw(COLORS.borderGray);
      setFill(COLORS.white);
      pdf.setLineWidth(0.25);
      pdf.roundedRect(margin, currentY, contentWidth, notesH, 1.3, 1.3, "FD");
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(6.8);
      setText(COLORS.slateLight);
      pdf.text(
        "Physician diagnosis summary & continuous lab evaluation remarks:",
        margin + 3,
        currentY + 4.2
      );
      setDraw(COLORS.borderGray);
      pdf.setLineWidth(0.15);
      [8.5, 12.5].forEach((offset) => {
        pdf.line(
          margin + 4,
          currentY + offset,
          margin + contentWidth - 4,
          currentY + offset
        );
      });
      currentY += notesH + 5;
    }

    // --- SECTION 8: SIGN-OFF (Signature / Stamp / QR) ----------------------
    drawSectionTitle("8. Verification & Authorization");
    drawSignOffBlock();

    // Footer (fixed position — single page)
    drawFooter();

    // ----------------------------------------------------------------------
    // FILE NAMING LOGIC (preserved exactly)
    // ----------------------------------------------------------------------
    const registrationDate = patient?.reg_date
      ? new Date(patient.reg_date).toLocaleDateString("en-GB").replace(/\//g, "-")
      : "No-Date";

    const fileName = `${patient?.patient_name || "Patient"}_${registrationDate}_${patient?.system_number || "File"
      }_report`
      .replace(/[\\/:*?"<>|]/g, "_") // Remove invalid filename characters
      .replace(/\s+/g, "_"); // Replace spaces with underscores

    pdf.save(`${fileName}.pdf`);
  };
  return (
    <div className="bg-slate-50 min-h-screen p-4 sm:p-8 font-sans antialiased">

      {/* ACTION CONTROLS TOP BAR */}
      <div className="flex items-center justify-between mb-6 max-w-5xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Patient Dossier</h1>
          <p className="text-xs font-medium text-slate-500 mt-0.5">
            System Identifier: <span className="text-slate-700 font-semibold">{system_number}</span>
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate(ROUTE_PATHS.PATIENT_LIST)}
            icon={ArrowLeft}
            className="flex items-center gap-2 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 shadow-sm px-4 py-2 rounded-lg transition text-sm font-medium"
            title="Back to List"
          />
          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium shadow-md shadow-green-100 px-4 py-2 rounded-lg transition text-sm"
          >
            <Printer className="w-4 h-4" />
            Download Clean PDF
          </button>
        </div>
      </div>

      {/* REVERTED TO BEAUTIFUL ORIGINAL GRADIENT/CARD COMPONENT LAYOUT */}
      <div
        ref={printRef}
        className="bg-white border border-slate-200 shadow-xl rounded-2xl max-w-5xl mx-auto overflow-hidden p-8 space-y-6"
      >
        {/* DOCUMENT HEADER BLOCK */}
        <div className="border-b border-slate-100 pb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-green-50 rounded-xl">
              <CompanyLogo className="h-12 w-auto text-green-600 company-logo-img" />
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">{website?.website_name}</h2>
              <p className="text-xs font-semibold uppercase tracking-wider text-green-600 mt-0.5">Official Clinical Medical Report</p>
            </div>
          </div>

          <div className="text-left sm:text-right text-xs space-y-1 bg-slate-50 border border-slate-100 p-3 rounded-xl min-w-[180px]">
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider">Registration Date</span>
              <span className="font-semibold text-slate-800 text-sm">
                {patient?.reg_date ? new Date(patient.reg_date).toLocaleDateString("en-GB") : "—"}
              </span>
            </div>
            <div className="pt-1.5 border-t border-slate-200 mt-1.5">
              <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider">Tracking Identifier</span>
              <span className="font-mono font-bold text-green-600">{patient?.system_number || "—"}</span>
            </div>
          </div>
        </div>

        {/* SECTION I: DEMOGRAPHICS CARD BLOCK */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-emerald-800 bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-100 w-fit">
            <User className="w-4 h-4 text-emerald-600" />
            <h3 className="text-xs font-bold uppercase tracking-wider">Patient Information</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 border border-slate-100 rounded-xl overflow-hidden shadow-sm text-xs">
            <div className="grid grid-cols-3 border-b md:border-b-0 md:border-r border-slate-100">
              <div className="bg-slate-50/50 p-3 font-semibold text-slate-500 border-r border-slate-100">Full Name</div>
              <div className="col-span-2 p-3 font-bold text-slate-800 bg-white">{patient?.patient_name || "—"}</div>

              <div className="bg-slate-50/50 p-3 font-semibold text-slate-500 border-r border-slate-100 border-t border-slate-100">Relative Reference</div>
              <div className="col-span-2 p-3 text-slate-700 bg-white border-t border-slate-100">{patient?.father_husband_name || "—"}</div>

              <div className="bg-slate-50/50 p-3 font-semibold text-slate-500 border-r border-slate-100 border-t border-slate-100">National ID (CNIC)</div>
              <div className="col-span-2 p-3 text-slate-700 font-mono bg-white border-t border-slate-100">{patient?.patient_cnic || "—"}</div>
            </div>

            <div className="grid grid-cols-3">
              <div className="bg-slate-50/50 p-3 font-semibold text-slate-500 border-r border-slate-100">Gender / Age</div>
              <div className="col-span-2 p-3 text-slate-700 bg-white">
                {patient?.gender || "—"} <span className="text-slate-300 mx-1.5">|</span> {patient?.age ? `${patient.age} Yrs` : "—"}
              </div>

              <div className="bg-slate-50/50 p-3 font-semibold text-slate-500 border-r border-slate-100 border-t border-slate-100">Mobile Contact</div>
              <div className="col-span-2 p-3 text-slate-700 font-mono bg-white border-t border-slate-100">{patient?.mobile || "—"}</div>

              <div className="bg-slate-50/50 p-3 font-semibold text-slate-500 border-r border-slate-100 border-t border-slate-100">Residential Address</div>
              <div className="col-span-2 p-3 text-slate-700 bg-white border-t border-slate-100 truncate" title={patient?.address}>
                {patient?.address || "—"}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION II: CLINICAL CONTEXT CARD BLOCK */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-green-800 bg-green-50 px-3 py-1.5 rounded-lg border border-green-100 w-fit">
            <Activity className="w-4 h-4 text-green-600" />
            <h3 className="text-xs font-bold uppercase tracking-wider">Clinical Context</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 border border-slate-100 rounded-xl overflow-hidden shadow-sm text-xs">
            <div className="grid grid-cols-3 border-b md:border-b-0 md:border-r border-slate-100">
              <div className="bg-slate-50/50 p-3 font-semibold text-slate-500 border-r border-slate-100">Admitting Physician</div>
              <div className="col-span-2 p-3 font-semibold text-slate-800 bg-white">{patient?.physician?.name || "—"}</div>

              <div className="bg-slate-50/50 p-3 font-semibold text-slate-500 border-r border-slate-100 border-t border-slate-100">District Region</div>
              <div className="col-span-2 p-3 text-slate-700 bg-white border-t border-slate-100">{patient?.unit?.district?.name || "—"}</div>
            </div>

            <div className="grid grid-cols-3">
              <div className="bg-slate-50/50 p-3 font-semibold text-slate-500 border-r border-slate-100">Assigned Unit</div>
              <div className="col-span-2 p-3 text-slate-700 bg-white">{patient?.unit?.name || "—"}</div>

              <div className="bg-slate-50/50 p-3 font-semibold text-slate-500 border-r border-slate-100 border-t border-slate-100">Classification</div>
              <div className="col-span-2 p-3 bg-white border-t border-slate-100 flex items-center">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-100">
                  {patient?.patient_type || "Standard Outpatient"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION III: VITALS DISPLAY TILES */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-rose-800 bg-rose-50 px-3 py-1.5 rounded-lg border border-rose-100 w-fit">
            <ShieldAlert className="w-4 h-4 text-rose-600" />
            <h3 className="text-xs font-bold uppercase tracking-wider">Physiological Vital Signs</h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: "Core Temp", val: patient?.temperature, unit: "°F", color: "border-l-orange-500" },
              { label: "Pulse Rate", val: patient?.pulse, unit: "BPM", color: "border-l-rose-500" },
              { label: "Blood Pressure", val: patient?.bp, unit: "mmHg", color: "border-l-purple-500" },
              { label: "Height metric", val: patient?.height, unit: "cm", color: "border-l-blue-500" },
              { label: "Mass Weight", val: patient?.weight, unit: "kg", color: "border-l-emerald-500" },
              { label: "ABO Group", val: patient?.blood_group, unit: "", color: "border-l-red-600" },
            ].map((v, idx) => (
              <div key={idx} className={`bg-slate-50 border border-slate-200/60 border-l-4 ${v.color} rounded-xl p-3 shadow-sm`}>
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block">{v.label}</span>
                <span className="text-base font-extrabold text-slate-800 mt-1 block">
                  {v.val || "—"}
                  <span className="text-xs font-normal text-slate-400 ml-0.5">{v.val && v.unit}</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* LOGISTICS BLOCK ROW COLUMNS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {/* LEFT PANEL: ICU / NICU DATA */}
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50 shadow-sm space-y-3 min-h-[165px]">
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b border-slate-200 pb-2">
              {patient?.patient_type === "icu" ? "Intensive Care Unit Metrics" : "Neonatal Intensive Care Metrics"}
            </h4>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-slate-400 block">Assigned Ward Location</span>
                <span className="font-semibold text-slate-700">{patient?.ward_type || "—"}</span>
              </div>
              <div>
                <span className="text-slate-400 block">Bed Assignment No</span>
                <span className="font-semibold text-slate-700">{patient?.bed || "—"}</span>
              </div>
              <div className="col-span-2 border-t border-slate-200 pt-2 grid grid-cols-2 gap-2">
                <div>
                  <span className="text-slate-400 block">Admission Timeline</span>
                  <span className="font-medium text-slate-700 text-[11px]">
                    {patient?.admission_date_time ? new Date(patient.admission_date_time).toLocaleString("en-GB") : "—"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block">Discharge Timeline</span>
                  <span className="font-medium text-slate-700 text-[11px]">
                    {patient?.discharge_date_time ? new Date(patient.discharge_date_time).toLocaleString("en-GB") : "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT PANEL: DYNAMIC SCROLLABLE VISITS HISTORY */}
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50 shadow-sm space-y-3 min-h-[165px] flex flex-col">
            <div className="flex items-center gap-1.5 border-b border-slate-200 pb-2 shrink-0">
              <Calendar className="w-4 h-4 text-slate-500" />
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Recent Evaluation Visits</h4>
            </div>

            {(!patient?.patient_visits || patient.patient_visits.length === 0) ? (
              <p className="text-xs text-slate-400 italic pt-2">No historical clinical evaluations logged.</p>
            ) : (
              /* max-h set karke custom scrollbar di hai taake 10+ visits par bhi card height expand hokar UI kharab na kare */
              <div className="space-y-2 text-xs overflow-y-auto max-h-[110px] pr-1 scrollbar-thin scrollbar-thumb-slate-200">
                {patient.patient_visits.map((v, i) => (
                  <div key={i} className="flex items-center justify-between py-1 border-b border-slate-100 last:border-0">
                    <span className="text-slate-700 font-medium">
                      • Visit: <span className="text-slate-900 font-bold">{v.visit_date ? new Date(v.visit_date).toLocaleDateString("en-GB") : "—"}</span>
                    </span>
                    <span className="text-slate-400 font-normal bg-white px-2 py-0.5 rounded-md border border-slate-100 text-[11px]">
                      Next Target: <span className="text-slate-600 font-semibold">{v.next_visit_plan ? new Date(v.next_visit_plan).toLocaleDateString("en-GB") : "—"}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* CLINICAL SUMMARY NOTES FIELD */}
        <div className="space-y-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">Clinical Diagnosis Summary & Notes</h3>
          <div className="border border-slate-200 rounded-xl p-4 h-24 bg-slate-50/30 relative">
            <p className="text-xs text-slate-400 italic">Physician consultation report summaries and diagnostic observations area layout wrapper...</p>
            <div className="w-full border-b border-dashed border-slate-200 mt-4"></div>
          </div>
        </div>

        <div className="space-y-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
            Clinical Diagnosis Summary & Notes
          </h3>

          <div className="border border-slate-200 rounded-xl p-4 h-24 bg-slate-50/30 relative">
            <p className="text-xs text-slate-400 italic">
              Physician consultation report summaries and diagnostic observations area layout wrapper...
            </p>
            <div className="w-full border-b border-dashed border-slate-200 mt-4"></div>
          </div>
        </div>

        {/* Attachment Card */}
        {patient?.attachments &&
          <MultiDocumentSelect files={patient?.attachments} isForDetail={true} />

        }

        {/* WEB VIEW CLOSING FOOTER BLOCK */}
        <div className="border-t border-slate-100 pt-4 flex flex-col sm:flex-row items-center justify-between text-[10px] text-slate-400 font-medium gap-2">
          <p>
            Office: {contactDetails?.email || "-"} • Support: {contactDetails?.phone || "-"}
          </p>
          <p className="tracking-wide uppercase font-semibold text-slate-300">Confidential Medical Chart Dossier Ledger</p>
        </div>
      </div>
    </div>

  );
};

export default PatientDetails;